import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

// Retention trend (BK, 10/9/26): monthly paying-member churn for this club against the baseline
// fixed 10/9/26 (Oct 2025-Sep 2026). Data comes from PlayByPoint's membership history, rebuilt
// nightly (backend src/retentionChurn.js); method in docs/COMS_Retention_Baseline_2026-10-09.md.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (m, withYear) => {
  const [y, mo] = m.split('-').map(Number);
  return withYear ? `${MONTHS[mo - 1]} ${y}` : MONTHS[mo - 1];
};
const GOOD = '#22c55e', BAD = '#ef4444', LINE = '#60a5fa';

function vsBaseline(v, base) {
  if (v == null || base == null) return 'var(--text)';
  return v <= base ? GOOD : BAD;
}

function TrendChart({ months, baseline, maxY, compact }) {
  const shown = months.slice(-13);
  const W = compact ? 380 : 640, H = compact ? 190 : 220, L = 34, R = 12, T = 14, B = 30;
  const max = maxY || Math.max(15, Math.ceil(Math.max(...shown.map(m => m.churnPct || 0), baseline?.churnPct || 0) / 5) * 5);
  const x = i => L + (shown.length === 1 ? (W - L - R) / 2 : (i * (W - L - R)) / (shown.length - 1));
  const y = v => T + (H - T - B) * (1 - v / max);
  const done = shown.map((m, i) => ({ ...m, i })).filter(m => m.complete);
  const current = shown.map((m, i) => ({ ...m, i })).find(m => !m.complete);
  const path = done.map((m, k) => `${k ? 'L' : 'M'}${x(m.i)},${y(m.churnPct)}`).join(' ');
  const ticks = [];
  for (let v = 0; v <= max; v += 5) ticks.push(v);
  const lastDone = done[done.length - 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
      aria-label="Monthly churn by month with the baseline">
      {ticks.map(v => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeWidth="1" />
          <text x={L - 6} y={y(v) + 4} textAnchor="end" fontSize="10" fill="var(--text-muted)">{v}%</text>
        </g>
      ))}
      {baseline?.churnPct != null && (
        <g>
          <line x1={L} x2={W - R} y1={y(baseline.churnPct)} y2={y(baseline.churnPct)}
            stroke="var(--text-muted)" strokeDasharray="5 4" strokeWidth="1.5" />
          {!compact && (
            <text x={W - R} y={y(baseline.churnPct) - 5} textAnchor="end" fontSize="10" fill="var(--text-muted)">
              Baseline {baseline.churnPct}%
            </text>
          )}
        </g>
      )}
      <path d={path} fill="none" stroke={LINE} strokeWidth="2.5" strokeLinejoin="round" />
      {current && lastDone && (
        <line x1={x(lastDone.i)} y1={y(lastDone.churnPct)} x2={x(current.i)} y2={y(current.churnPct)}
          stroke={LINE} strokeWidth="1.5" strokeDasharray="3 3" />
      )}
      {done.map(m => (
        <circle key={m.month} cx={x(m.i)} cy={y(m.churnPct)} r={m.note ? 5 : 4} fill={vsBaseline(m.churnPct, baseline?.churnPct)}
          stroke="var(--surface, #111)" strokeWidth="1.5">
          <title>{`${monthLabel(m.month, true)}: ${m.churnPct}% (${m.lost} of ${m.payingStart} paying members)${m.note ? ` - ${m.note}` : ''}`}</title>
        </circle>
      ))}
      {current && (
        <g>
          <circle cx={x(current.i)} cy={y(current.churnPct)} r="4.5" fill="none" stroke={LINE} strokeWidth="2">
            <title>{`${monthLabel(current.month, true)} so far: ${current.churnPct}% (${current.lost} of ${current.payingStart})`}</title>
          </circle>
          <text x={x(current.i) + (current.i === shown.length - 1 ? 6 : 0)} y={y(current.churnPct) - 9} textAnchor={current.i === shown.length - 1 ? 'end' : 'middle'} fontSize="10" fill="var(--text-muted)">so far</text>
        </g>
      )}
      {shown.map((m, i) => ((!compact || i % 2 === (shown.length - 1) % 2) && (
        <text key={m.month} x={x(i)} y={H - 10} textAnchor="middle" fontSize="10" fill="var(--text-muted)">
          {monthLabel(m.month, !compact && (i === 0 || m.month.endsWith('-01-01')))}
        </text>
      )))}
    </svg>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || 'var(--text)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}

const CLUB_NAMES = { DEN: 'Denton', LEW: 'Lewisville', MCK: 'McKinney', GB: 'Green Bay', MF: 'Menomonee Falls', WA: 'West Allis' };
const CLUB_ORDER = ['DEN', 'LEW', 'MCK', 'GB', 'MF', 'WA'];

// Admin only (BK, 10/9/26): all six clubs side by side, plus all clubs combined.
function AllClubsView() {
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    get('/club-dashboard/retention-trend-all').then(setData).catch(e => setError(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div style={{ color: 'var(--red)', padding: 8 }}>All-clubs trend failed to load: {error}</div>;
  if (!data) return <div className="spinner" />;
  if (!data.available) return <div style={{ color: 'var(--text-muted)' }}>The retention trend starts after the first nightly run.</div>;

  const clubs = [...data.clubs].sort((a, b) => CLUB_ORDER.indexOf(a.locationId) - CLUB_ORDER.indexOf(b.locationId));
  const rowsFor = t => {
    const done = t.months.filter(m => m.complete);
    return { last: done[done.length - 1], current: t.months.find(m => !m.complete) };
  };
  const shared = Math.max(15, Math.ceil(Math.max(...clubs.flatMap(c => c.months.slice(-13).map(m => m.churnPct || 0))) / 5) * 5);
  const cell = { padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' };
  const summaryRow = (t, name, bold) => {
    const { last, current } = rowsFor(t);
    const b = t.baseline;
    return (
      <tr key={t.locationId} style={{ borderTop: '1px solid var(--border)', fontWeight: bold ? 700 : 400 }}>
        <td style={{ ...cell, textAlign: 'left' }}>{name}</td>
        <td style={cell}>{b?.churnPct ?? '-'}%</td>
        <td style={{ ...cell, color: vsBaseline(last?.churnPct, b?.churnPct) }}>{last ? `${last.churnPct}%` : '-'}</td>
        <td style={cell}>{last ? `${last.lost} of ${last.payingStart}` : '-'}</td>
        <td style={cell}>{current ? `${current.churnPct}% (${current.lost})` : '-'}</td>
        <td style={cell}>{b?.newMemberChurnPct ?? '-'}%</td>
        <td style={{ ...cell, color: vsBaseline(last?.newMemberChurnPct, b?.newMemberChurnPct) }}>{last?.newMemberChurnPct ?? '-'}%</td>
        <td style={cell}>{b?.establishedChurnPct ?? '-'}%</td>
        <td style={cell}>{b?.leftAllClubsPct ?? '-'}%</td>
        <td style={cell}>{(current || last)?.membersEnd ?? '-'}</td>
      </tr>
    );
  };

  return (
    <div>
      <div style={{ overflowX: 'auto', marginBottom: 16 }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'var(--text-muted)' }}>
              <th style={{ ...cell, textAlign: 'left' }}>Club</th>
              <th style={cell}>Baseline</th>
              <th style={cell}>Last month</th>
              <th style={cell}>Lost</th>
              <th style={cell}>This month so far</th>
              <th style={cell}>New-member baseline</th>
              <th style={cell}>New members last month</th>
              <th style={cell}>Established baseline</th>
              <th style={cell} title="Baseline counting only people who left every club (a move between clubs is not a loss)">Left all clubs</th>
              <th style={cell} title="Everyone with access today, including Welld and comped">Members now</th>
            </tr>
          </thead>
          <tbody>
            {clubs.map(t => summaryRow(t, CLUB_NAMES[t.locationId] || t.locationId))}
            {summaryRow(data.all, 'All clubs', true)}
          </tbody>
        </table>
      </div>

      <div style={{ fontWeight: 700, margin: '4px 0 6px' }}>All clubs combined</div>
      <TrendChart months={data.all.months} baseline={data.all.baseline} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginTop: 16 }}>
        {clubs.map(t => (
          <div key={t.locationId} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 10px 4px' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
              {CLUB_NAMES[t.locationId] || t.locationId}
              <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · baseline {t.baseline?.churnPct ?? '-'}%</span>
            </div>
            <TrendChart months={t.months} baseline={t.baseline} maxY={shared} compact />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
        Club charts share one scale so they can be compared. The all-clubs baseline pools each club's own baseline months.
      </div>
    </div>
  );
}

export default function RetentionTrendPanel({ clubId, clubName, isAdmin }) {
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState('club');

  useEffect(() => {
    if (!clubId) return;
    setData(null); setError(null);
    get(`/club-dashboard/retention-trend?locationId=${clubId}`).then(setData).catch(e => setError(e.message));
  }, [clubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const tab = (key, label) => (
    <button key={key} onClick={() => setView(key)}
      style={{ background: view === key ? 'var(--surface-2)' : 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '3px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
      {label}
    </button>
  );
  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700 }}>
          Paying-member churn by month{view === 'all' ? ' · All clubs' : clubName ? ` · ${clubName}` : ''}
        </div>
        {isAdmin && <div style={{ display: 'flex', gap: 6 }}>{tab('club', 'This club')}{tab('all', 'All clubs')}</div>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>From PlayByPoint, updated nightly</div>
    </div>
  );
  if (isAdmin && view === 'all') {
    return <div className="card" style={{ padding: '16px 18px' }}>{header}<AllClubsView /></div>;
  }

  const shell = (body) => <div className="card" style={{ padding: '16px 18px' }}>{header}{body}</div>;
  if (error) return shell(<div style={{ color: 'var(--red)' }}>Retention trend failed to load: {error}</div>);
  if (!data) return shell(<div className="spinner" />);
  if (!data.available || !data.months.length) {
    return shell(<div style={{ color: 'var(--text-muted)' }}>The retention trend starts after the first nightly run.</div>);
  }

  const { baseline, months } = data;
  const done = months.filter(m => m.complete);
  const last = done[done.length - 1];
  const current = months.find(m => !m.complete);
  const notes = months.filter(m => m.note);

  return (
    <div className="card" style={{ padding: '16px 18px' }}>
      {header}

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
        {baseline && (
          <Stat label="Baseline" value={`${baseline.churnPct}%`}
            sub={`${monthLabel(baseline.from, true)} to ${monthLabel(baseline.to, true)} average`} />
        )}
        {last && (
          <Stat label={`Last month (${monthLabel(last.month)})`} value={`${last.churnPct}%`}
            color={vsBaseline(last.churnPct, baseline?.churnPct)} sub={`${last.lost} of ${last.payingStart} paying members`} />
        )}
        {current && (
          <Stat label={`${monthLabel(current.month)} so far`} value={`${current.churnPct}%`}
            sub={`${current.lost} of ${current.payingStart} lost`} />
        )}
        {baseline && last && (
          <Stat label="New members (first 90 days)" value={`${last.newMemberChurnPct ?? '-'}%`}
            color={vsBaseline(last.newMemberChurnPct, baseline.newMemberChurnPct)}
            sub={`last month · baseline ${baseline.newMemberChurnPct}%`} />
        )}
      </div>

      <TrendChart months={months} baseline={baseline} />

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
        Churn = paying members on the 1st who no longer had a membership on the 1st of the next month. Green dots are at or
        below the baseline, red above. Welld and comped members are not included.
        {notes.map(n => <div key={n.month}>{monthLabel(n.month, true)}: {n.note}</div>)}
      </div>

      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>Show the numbers by month</summary>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr style={{ color: 'var(--text-muted)', textAlign: 'right' }}>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>Month</th>
                <th style={{ padding: '4px 6px' }}>Paying on the 1st</th>
                <th style={{ padding: '4px 6px' }}>Lost</th>
                <th style={{ padding: '4px 6px' }}>Churn</th>
                <th style={{ padding: '4px 6px' }}>New members' churn</th>
                <th style={{ padding: '4px 6px' }}>Established churn</th>
                <th style={{ padding: '4px 6px' }}>Joined</th>
                <th style={{ padding: '4px 6px' }}>Came back</th>
              </tr>
            </thead>
            <tbody>
              {[...months].reverse().map(m => (
                <tr key={m.month} style={{ borderTop: '1px solid var(--border)', textAlign: 'right' }}>
                  <td style={{ textAlign: 'left', padding: '4px 6px' }}>{monthLabel(m.month, true)}{m.complete ? '' : ' (so far)'}</td>
                  <td style={{ padding: '4px 6px' }}>{m.payingStart}</td>
                  <td style={{ padding: '4px 6px' }}>{m.lost}</td>
                  <td style={{ padding: '4px 6px', color: m.complete ? vsBaseline(m.churnPct, baseline?.churnPct) : 'var(--text)' }}>{m.churnPct}%</td>
                  <td style={{ padding: '4px 6px' }}>{m.newMemberChurnPct ?? '-'}{m.newMemberChurnPct != null ? '%' : ''}</td>
                  <td style={{ padding: '4px 6px' }}>{m.establishedChurnPct ?? '-'}{m.establishedChurnPct != null ? '%' : ''}</td>
                  <td style={{ padding: '4px 6px' }}>{m.newMembers}</td>
                  <td style={{ padding: '4px 6px' }}>{m.returned}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
